import { Box } from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { RichTextEditor } from 'components/RichTextEditor';
import { ReporterNotesList } from 'features/reporterNotes/components/ReporterNotesList';
import {
  ReporterNote,
  ReporterNotesFormValues,
} from 'features/reporterNotes/reporterNotes.types';
import {
  isReporterNoteArchived,
  toggleReporterNoteArchived,
} from 'features/reporterNotes/reporterNotes.utils';
import { ReporterNoteActionMenu } from './ReporterNoteActionMenu';

type ReporterNotesFormSectionProps = {
  geoEventId?: number;
  newReporterNoteInitialValue: string;
};

export const ReporterNotesFormSection = (
  props: ReporterNotesFormSectionProps,
): JSX.Element => {
  const { geoEventId, newReporterNoteInitialValue } = props;
  const { t } = useTranslation();
  const { control, formState, setValue } =
    useFormContext<ReporterNotesFormValues>();
  const reporterNoteToggles = useWatch({
    control,
    name: 'reporterNoteToggles',
  });

  const renderNoteAction = (note: ReporterNote): JSX.Element => (
    <ReporterNoteActionMenu
      isArchived={isReporterNoteArchived(note, reporterNoteToggles)}
      disabled={formState.isSubmitting}
      onToggleArchived={() =>
        setValue(
          'reporterNoteToggles',
          toggleReporterNoteArchived(note, reporterNoteToggles),
          { shouldDirty: true },
        )
      }
    />
  );

  return (
    <Box>
      <ReporterNotesList
        geoEventId={geoEventId}
        pendingToggles={reporterNoteToggles}
        renderNoteAction={renderNoteAction}
        disableGutters
      />
      <Box sx={{ pt: 2 }}>
        <Controller
          name="newReporterNote"
          control={control}
          render={({ field }): JSX.Element => (
            <RichTextEditor
              id="field-control-new-reporter-note"
              data-testid="new-reporter-note"
              label={t('reporterNotes.newNote.label')}
              initialValue={newReporterNoteInitialValue}
              onChange={field.onChange}
              onBlur={field.onBlur}
              editable={!formState.isSubmitting}
            />
          )}
        />
      </Box>
    </Box>
  );
};
